import { useEffect, useRef, useState } from 'react';

export interface TermLine {
  id: string;
  text: string;
  variant?: 'system' | 'ok' | 'error' | 'amber' | 'task';
  /** השהיה נוספת (ms) אחרי שהשורה מסתיימת, לפני שמתחילים להקליד את הבאה */
  pauseAfterMs?: number;
}

interface TypewriterState {
  /** שורות שהושלמו במלואן */
  completed: TermLine[];
  /** השורה הנוכחית בהקלדה (טקסט חלקי) */
  typing: { line: TermLine; revealed: string } | null;
  done: boolean;
}

// בערך פי 2 מהר מההקלדה המקורית (34ms לתו / 900ms לשורה) — לבקשת המשתמש: "הכול כמו
// מקודם, רק קצת יותר מהיר"
const MIN_DELAY = 4;
const MAX_DELAY = 17;
/** תקציב זמן הקלדה כולל יעד לשורה ארוכה — כדי שמשימות ארוכות לא יקלידו לנצח */
const TARGET_LINE_MS = 450;
const DEFAULT_PAUSE_MS = 110;

function delayForLine(length: number): number {
  if (length <= 0) return MIN_DELAY;
  const perChar = TARGET_LINE_MS / length;
  return Math.min(MAX_DELAY, Math.max(MIN_DELAY, perChar));
}

/**
 * מקליד שורות טקסט אחת אחרי השנייה באפקט טרמינל אמיתי.
 * כאשר enabled=false (הגדרת אנימציות כבויה / prefers-reduced-motion) — מציג הכול מיידית.
 */
export function useTypewriter(
  lines: TermLine[],
  enabled: boolean,
  onDone?: () => void,
  onLineComplete?: (line: TermLine) => void,
): TypewriterState {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState('');
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const onLineCompleteRef = useRef(onLineComplete);
  onLineCompleteRef.current = onLineComplete;
  const linesKey = lines.map((l) => l.id).join('|');

  useEffect(() => {
    setIndex(0);
    setRevealed('');
  }, [linesKey]);

  useEffect(() => {
    if (!enabled) {
      setIndex(lines.length);
      setRevealed('');
      if (lines.length > 0) {
        for (const l of lines) onLineCompleteRef.current?.(l);
        onDoneRef.current?.();
      }
      return;
    }
    if (index >= lines.length) {
      if (lines.length > 0) onDoneRef.current?.();
      return;
    }
    const current = lines[index]!;
    if (revealed.length >= current.text.length) {
      onLineCompleteRef.current?.(current);
      const t = setTimeout(() => {
        setIndex((i) => i + 1);
        setRevealed('');
      }, current.pauseAfterMs ?? DEFAULT_PAUSE_MS);
      return () => clearTimeout(t);
    }
    const delay = delayForLine(current.text.length);
    const t = setTimeout(() => {
      setRevealed(current.text.slice(0, revealed.length + 1));
    }, delay);
    return () => clearTimeout(t);
  }, [enabled, index, revealed, lines]);

  const completed = enabled ? lines.slice(0, index) : lines;
  const typing = enabled && index < lines.length ? { line: lines[index]!, revealed } : null;
  const done = index >= lines.length;

  return { completed, typing, done };
}
